import React from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingDown, DollarSign, Calculator, Sparkles, Check } from "lucide-react";

/**
 * Plan adjustment factors vs market avg (controls how much cheaper Velcor is).
 * Same constants used in PriceBreakdown so the math stays consistent.
 */
// Plan adjustments represent what the customer PAYS vs baseline.
// flexible = 10% savings (the user's requested target), basic more savings,
// plus with premium features so slightly smaller discount.
export const PLAN_ADJUSTMENTS = { basic: 0.85, flexible: 0.9, plus: 0.95 };
const MARKET_PREMIUM = 1.0; // Baseline is the real estimate; no artificial premium

const fmt = (n) => (n != null && Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

/**
 * Compute monthly savings for a given plan and (optional) current bill.
 * If currentBill provided → savings vs that. Otherwise → savings vs Texas market avg.
 */
export function computeSavings({ estimate, plan, currentBill }) {
  const monthlyBill = estimate?.estimated_monthly_bill;
  if (!monthlyBill) return null;

  const monthlyKwh = estimate?.annual_kwh ? estimate.annual_kwh / 12 : 1000;
  const allInRate = (monthlyBill / monthlyKwh) * 100; // ¢/kWh

  const planAdj = PLAN_ADJUSTMENTS[plan] ?? 1.0;
  const velcorMonthly = (allInRate * planAdj * monthlyKwh) / 100;

  const baseline = currentBill && currentBill > 0 ? currentBill : monthlyBill * MARKET_PREMIUM;

  const savingsMonth = baseline - velcorMonthly;
  const savingsYear = savingsMonth * 12;
  const savingsPct = baseline > 0 ? (savingsMonth / baseline) * 100 : 0;

  return {
    baseline,
    velcorMonthly,
    savingsMonth,
    savingsYear,
    savingsPct,
    isCustom: Boolean(currentBill && currentBill > 0),
    monthlyKwh,
  };
}

export default function SavingsCalculator({ estimate, plan, currentBill, onCurrentBillChange }) {
  const { t } = useTranslation();
  const result = computeSavings({ estimate, plan, currentBill });

  if (!result) return null;

  const positive = result.savingsMonth > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-strong rounded-[28px] p-5 md:p-7 relative overflow-hidden"
      data-testid="savings-calculator"
      style={{
        background:
          "linear-gradient(135deg, rgba(47,122,58,0.06) 0%, rgba(47,122,58,0.02) 60%, rgba(255,255,255,0.92) 100%)",
        borderColor: "rgba(47,122,58,0.18)",
      }}
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="absolute -right-16 -top-16 w-64 h-64 rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(47,155,67,0.35), transparent 65%)", filter: "blur(28px)" }}
      />

      <div className="relative">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <span className="badge-pill mb-2">
              <Calculator className="w-3.5 h-3.5 mr-1.5" />
              {result.isCustom ? t("plans.savingsCustomTitle") : t("plans.savingsTitle")}
            </span>
            <p className="text-slate-500 text-[13px] max-w-[480px] leading-relaxed">
              {result.isCustom
                ? t("plans.savingsCustomLead", { bill: fmt(currentBill) })
                : t("plans.savingsLead")}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 md:gap-4">
          {/* Current / market */}
          <div className="rounded-2xl p-4 bg-white/60 border border-slate-200" data-testid="savings-current">
            <div className="text-[10.5px] font-black uppercase tracking-wider text-slate-400 mb-1">
              {result.isCustom ? t("plans.savingsBillLabel") : t("plans.savingsCurrent")}
            </div>
            <div className="display text-[34px] md:text-[40px] text-slate-700 leading-none line-through decoration-slate-300/70">
              {fmt(result.baseline)}
            </div>
            <div className="text-[11px] font-bold text-slate-400 mt-1">
              {result.isCustom ? t("plans.savingsPerMonth").replace("/", "") : t("plans.savingsCurrentHint")}
            </div>
          </div>

          {/* Velcor */}
          <div className="rounded-2xl p-4 bg-velcor-ink text-white relative" data-testid="savings-velcor">
            <div className="text-[10.5px] font-black uppercase tracking-wider text-velcor-green-light mb-1">
              {t("plans.savingsVelcor")} · <span className="capitalize">{plan}</span>
            </div>
            <div className="display text-[34px] md:text-[40px] leading-none">{fmt(result.velcorMonthly)}</div>
            <div className="text-[11px] font-bold text-velcor-green-light mt-1">
              {t("plans.savingsPerMonth").replace("/", "")}
            </div>
          </div>

          {/* Savings */}
          <div
            className={`rounded-2xl p-4 relative overflow-hidden border-2 ${
              positive ? "border-velcor-green/30" : "border-rose-200"
            }`}
            style={{
              background: positive
                ? "linear-gradient(135deg, #2f7a3a, #25662f)"
                : "linear-gradient(135deg, #fff1f2, #ffe4e6)",
            }}
            data-testid="savings-result"
          >
            <div
              className={`text-[10.5px] font-black uppercase tracking-wider mb-1 ${
                positive ? "text-velcor-green-light" : "text-rose-600"
              }`}
            >
              {positive ? t("plans.savingsYouSave") : t("plans.savingsNotSaving")}
            </div>
            <div className="flex items-baseline gap-2">
              <div className={`display text-[34px] md:text-[44px] leading-none ${positive ? "text-white" : "text-rose-700"}`}>
                {positive ? fmt(result.savingsMonth) : fmt(Math.abs(result.savingsMonth))}
              </div>
              <span className={`text-[11.5px] font-extrabold ${positive ? "text-velcor-green-light" : "text-rose-600"}`}>
                /mes
              </span>
            </div>
            {positive && (
              <div className="text-[12px] font-extrabold text-white/95 mt-1.5 inline-flex items-center gap-1">
                <TrendingDown className="w-3.5 h-3.5" />
                {fmt(result.savingsYear)} {t("plans.savingsPerYear")} · {Math.round(result.savingsPct)}%
              </div>
            )}
          </div>
        </div>

        {/* Custom bill input */}
        <div
          className="mt-5 rounded-2xl bg-white/85 border border-slate-200 p-3 md:p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3"
          data-testid="savings-custom-input"
        >
          <label className="font-extrabold text-[13px] text-slate-700 inline-flex items-center gap-2 shrink-0">
            <DollarSign className="w-4 h-4 text-velcor-green" />
            {t("plans.savingsBillLabel")}
          </label>
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-slate-400">$</span>
              <input
                type="number"
                min={0}
                step={1}
                inputMode="decimal"
                value={currentBill || ""}
                onChange={(e) => onCurrentBillChange(e.target.value ? Number(e.target.value) : null)}
                placeholder={t("plans.savingsBillPlaceholder")}
                className="w-full h-11 pl-7 pr-4 rounded-xl border border-slate-200 bg-white outline-none focus:border-velcor-green text-[14px] font-bold"
                data-testid="savings-bill-input"
              />
            </div>
            <AnimatePresence>
              {result.isCustom && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="pill px-3 h-11 bg-velcor-green/10 text-velcor-green text-[12px] font-extrabold border border-velcor-green/20 inline-flex items-center"
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  {Math.round(result.savingsPct)}%
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <span className="text-[11px] text-slate-400 font-bold inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {t("plans.savingsLead").split(".")[0]}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
